// START IMPORTS
import { Stack, Box, useTheme } from '@ltvco/refresh-lib/theme';
import { DateUtil } from '@ltvco/refresh-lib/utils';
import {
  useSession,
  useDarkWebReport,
  ReportLoading,
  type SerializedDarkWebBreach,
} from '@ltvco/refresh-lib/v1';
import {
  getEmailPermalink,
  DarkWebBreachTips,
  DarkWebCoverage,
  DarkWebOverview,
  DarkWebResults as DarkWebResultsV2,
  DarkWebScanFAQ as DarkWebFAQV2,
  FeatureBlockedModal,
  ReportNavigationMenu,
  ReportNavigationMenuProps,
  ReportNavigationMobileMenu,
  useFeatureBlockedModal,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getDarkWebNavLinkData } from 'navLinkData/darkWebNavLinkData';

// END IMPORTS

export function DarkWebScan() {
  // START HOOKS_AND_QUERIES
  const navigate = useNavigate();
  const isInDarkWebUpsellVariation = useFeatureIsOn('OAR-2154');
  const {
    isFeatureBlockedModalOpen,
    openFeatureBlockedModal,
    closeFeatureBlockedModal,
  } = useFeatureBlockedModal();
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  const {
    session: { account },
  } = useSession();
  const [isDarkWebSectionLoading, setIsDarkWebSectionLoading] = useState(false);
  const [permalink, setPermalink] = useState('');

  const email = account?.account.user_info?.email ?? '';
  const { data: darkWebData, isLoading } = useDarkWebReport({ email });
  const dateUtil = new DateUtil();
  const theme = useTheme();
  // END HOOKS_AND_QUERIES

  // START DERIVED_DATA_AND_UTILS
  const darkWebNavLinkData = getDarkWebNavLinkData(theme);
  const delayDarkWebLoading = 1000;
  const breaches = darkWebData?.data?.breaches ?? [];
  const firstBreachDate = breaches?.[0]?.date ?? '';
  const updatedAt = darkWebData?.data?.rawData?.meta?.updated_at;
  const createdAt = darkWebData?.data?.rawData?.meta?.created_at ?? '';
  const isSensitive = breaches?.some((breach) => breach?.isSensitive);

  const { year } = dateUtil.parseDatePartsFromString(
    createdAt,
    'yyyy-MM-dd HH:mm:ss ZZZ'
  );
  // END DERIVED_DATA_AND_UTILS

  // START EFFECTS
  useEffect(() => {
    document.title = 'Dark Web Scan - BeenVerified';
  }, []);

  useEffect(() => {
    if (isInDarkWebUpsellVariation) openFeatureBlockedModal();
  }, [isInDarkWebUpsellVariation]);

  useEffect(() => {
    setIsDarkWebSectionLoading(true);
    const id = setTimeout(() => {
      setIsDarkWebSectionLoading(false);
    }, delayDarkWebLoading * 10);

    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    (async () => {
      if (email) {
        const formattedEmail = encodeURIComponent(email.trim());
        const data = await getEmailPermalink(formattedEmail);
        setPermalink(data?.permalink ?? '');
      }
    })();
  }, [email]);
  // END EFFECTS

  // START EARLY_RETURNS
  if (isInDarkWebUpsellVariation) {
    return (
      <FeatureBlockedModal
        isOpen={isFeatureBlockedModalOpen}
        onClose={() => {
          closeFeatureBlockedModal();
          window.history.length > 1
            ? navigate(-1)
            : navigate('/dashboard', { replace: true });
        }}
        searchType="Dark Web"
        paymentShowModalNumber={paymentShowModalNumber}
        setPaymentShowModalNumber={setPaymentShowModalNumber}
      />
    );
  }

  if (isLoading) {
    return <ReportLoading />;
  }
  // END EARLY_RETURNS

  // START RENDER
  return (
    <Box marginBottom={2.5}>
      <Stack direction="row">
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Dark Web Scan"
            headerTitle={email}
            menuItems={
              darkWebNavLinkData as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        <Box marginTop={0} width="100%">
          <Stack
            spacing={2}
            padding={(theme) => ({
              xs: theme.spacing(2.5),
              md: `${theme.spacing(2.5)} 0`,
            })}
          >
            <DarkWebOverview
              email={email}
              updated={updatedAt ?? ''}
              breaches={breaches?.length ?? 0}
              created={firstBreachDate || year?.toString() || 'Unknown'}
              isSensitive={!!isSensitive}
            />
            <DarkWebResultsV2
              email={email}
              breaches={breaches as SerializedDarkWebBreach[]}
              loading={isDarkWebSectionLoading}
              delayDuration={delayDarkWebLoading}
            />
            <DarkWebBreachTips permalink={permalink} />
            <DarkWebFAQV2 />
            <DarkWebCoverage />
          </Stack>
        </Box>
      </Stack>
      <ReportNavigationMobileMenu
        reportType="Dark Web Scan"
        headerTitle={email}
        menuItems={darkWebNavLinkData as ReportNavigationMenuProps['menuItems']}
      />
    </Box>
  );
}
// END RENDER
